import {
  keepPreviousData,
  skipToken,
  useMutation,
  useQuery,
  useQueryClient
} from '@tanstack/react-query'
import { useCallback } from 'react'
import { GetVlmResultsParams, vlmApi } from '@/api/vlmApi'
import { WithListParams } from '@/types/pagination'

export const vlmQueryKeys = {
  all: ['vlm'] as const,
  modelList: (params?: WithListParams) =>
    [...vlmQueryKeys.all, 'model-list', params] as const,
  pipelineList: (params?: WithListParams) =>
    [...vlmQueryKeys.all, 'pipeline-list', params] as const,
  resultList: (params?: GetVlmResultsParams) =>
    [...vlmQueryKeys.all, 'result-list', params] as const,
  milestoneServerList: (params?: WithListParams) =>
    [...vlmQueryKeys.all, 'milestone-server-list', params] as const,
  resultDetail: (resultId?: string) =>
    [...vlmQueryKeys.all, 'result-detail', resultId] as const,
  milestoneServerCameras: (serverId?: string, deviceId?: string) =>
    [
      ...vlmQueryKeys.all,
      'milestone-server-cameras',
      serverId,
      deviceId
    ] as const
}

export const useInvalidateVlmPipelines = () => {
  const queryClient = useQueryClient()

  const invalidateVlmPipelineList = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [...vlmQueryKeys.all, 'pipeline-list']
    })
  }, [queryClient])

  return { invalidateVlmPipelineList }
}

export const useInvalidateMilestoneServers = () => {
  const queryClient = useQueryClient()

  const invalidateMilestoneServerList = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [...vlmQueryKeys.all, 'milestone-server-list']
    })
  }, [queryClient])

  return { invalidateMilestoneServerList }
}

export const useCreateVlmPipeline = () => {
  return useMutation({
    mutationFn: vlmApi.createVlmPipeline
  })
}

export const useUpdateVlmPipeline = () => {
  return useMutation({
    mutationFn: vlmApi.updateVlmPipeline
  })
}

export const useDeleteVlmPipeline = () => {
  return useMutation({
    mutationFn: vlmApi.deleteVlmPipeline
  })
}

export const useRestartVlmPipeline = () => {
  return useMutation({
    mutationFn: vlmApi.restartVlmPipeline
  })
}

export const useGetVlmModelList = (
  params: WithListParams,
  enabled?: boolean
) => {
  return useQuery({
    queryKey: vlmQueryKeys.modelList(params),
    queryFn: () => vlmApi.getVlmModelList(params),
    enabled
  })
}

export const useGetVlmPipelineList = (
  params: WithListParams,
  options?: { keepPreviousData?: boolean }
) => {
  return useQuery({
    queryKey: vlmQueryKeys.pipelineList(params),
    queryFn: () => vlmApi.getVlmPipelineList(params),
    placeholderData: options?.keepPreviousData ? keepPreviousData : undefined
  })
}

export const useGetVlmResultList = (
  params: GetVlmResultsParams,
  options?: { keepPreviousData?: boolean }
) => {
  return useQuery({
    queryKey: vlmQueryKeys.resultList(params),
    queryFn: () => vlmApi.getVlmResultList(params),
    placeholderData: options?.keepPreviousData ? keepPreviousData : undefined
  })
}

// Media URLs expire in 15 min, so refetch per drawer open. No retry: a deleted
// pipeline's event 404s here.
export const useGetVlmResult = (resultId?: string) => {
  return useQuery({
    queryKey: vlmQueryKeys.resultDetail(resultId),
    queryFn: resultId ? () => vlmApi.getVlmResult(resultId) : skipToken,
    staleTime: 0,
    gcTime: 0,
    retry: false
  })
}

export const useGetMilestoneServerList = (
  params: WithListParams,
  enabled = true
) => {
  return useQuery({
    queryKey: vlmQueryKeys.milestoneServerList(params),
    queryFn: () => vlmApi.getMilestoneServerList(params),
    enabled
  })
}

export const useCreateMilestoneServer = () => {
  return useMutation({
    mutationFn: vlmApi.createMilestoneServer
  })
}

export const useDeleteMilestoneServer = () => {
  return useMutation({
    mutationFn: vlmApi.deleteMilestoneServer
  })
}

// A POST, but it only reads: the device lists the server's cameras. No retry:
// a failure is usually wrong credentials or an outdated edge client, and each
// attempt can take ~30 s.
export const useListMilestoneServerCameras = ({
  serverId,
  deviceId
}: {
  serverId?: string
  deviceId?: string
}) => {
  return useQuery({
    queryKey: vlmQueryKeys.milestoneServerCameras(serverId, deviceId),
    queryFn:
      serverId && deviceId
        ? () => vlmApi.listMilestoneServerCameras(serverId, deviceId)
        : skipToken,
    staleTime: 60_000,
    retry: false
  })
}

export const useTestMilestoneConnection = () => {
  return useMutation({
    mutationFn: vlmApi.testMilestoneConnection
  })
}
